import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { Lock } from 'lucide-react'
import { useState } from 'react'
import type { OutreachContactDecision } from '../api'
import { useLaunchFlow } from '../context/launch-flow-context'
import {
  useOutreachContactDecisionsQuery,
  useSetOutreachContactDecisionsMutation,
} from '../hooks/use-outreach-contact-decisions'
import { useOutreachMessagesQuery } from '../hooks/use-outreach-messages'
import {
  CONTACTS_BOARD_ID,
  QueuedApprovalDialog,
  RosterKanban,
  type RosterMatch,
} from './outreach-active-view'
import { OutreachSetupCta } from './outreach-setup-cta'

// How many real contacts are revealed while the list is still locked.
const LOCKED_PREVIEW_COUNT = 2

// The pre-launch "Who we'll contact" board, rendered with the real campaign
// roster (RosterKanban). Locked until the profile step is done: the top 2
// contacts as a carrot, the rest blurred behind the unlock CTA. Once unlocked,
// every best-fit contact renders in full. Per-contact approve/decline is
// persisted server-side (keyed by investor + contact) so decisions survive a
// refresh and are applied at launch — declined contacts are excluded, approved
// ones skip the post-launch review step. Contacts show a badge only once the
// founder actually decides (no "Approved" default), so approving is visible.
export function OutreachPreLaunchBoard({
  entries,
  totalContacts,
  contactsEstimated = false,
}: {
  entries: RosterMatch[]
  totalContacts: number
  contactsEstimated?: boolean
}) {
  const { startupId, checklist } = useLaunchFlow()
  const [detail, setDetail] = useState<RosterMatch | null>(null)
  const [confirmApproveAll, setConfirmApproveAll] = useState(false)
  const messagesQuery = useOutreachMessagesQuery(startupId)
  const decisionsQuery = useOutreachContactDecisionsQuery(startupId)
  const setDecisions = useSetOutreachContactDecisionsMutation(startupId)

  const locked = !checklist.steps.some((step) => step.id === 'profile' && step.done)

  const visible = locked ? entries.slice(0, LOCKED_PREVIEW_COUNT) : entries

  // Server-persisted decisions, keyed by `${investorId}:${contactId}`.
  const decisionByKey = new Map<string, 'approved' | 'declined'>()
  for (const d of decisionsQuery.data ?? []) {
    decisionByKey.set(
      `${d.investorId}:${d.contactId}`,
      d.decision === 'APPROVED' ? 'approved' : 'declined'
    )
  }
  const decisionOf = (entry: RosterMatch): 'approved' | 'declined' | undefined =>
    entry.contactId ? decisionByKey.get(`${entry.investorId}:${entry.contactId}`) : undefined

  // Persist a decision for one or more contacts. The mutation updates the cache
  // optimistically, so badges flip instantly. Fund-level placeholders carry no
  // contactId (the partner isn't researched yet), so they can't be persisted.
  const persist = (targets: RosterMatch[], decision: 'approved' | 'declined') => {
    const kind = decision === 'approved' ? 'APPROVED' : 'DECLINED'
    const payload: OutreachContactDecision[] = targets.flatMap((entry) =>
      entry.contactId
        ? [{ investorId: entry.investorId, contactId: entry.contactId, decision: kind }]
        : []
    )
    if (payload.length > 0) setDecisions.mutate(payload)
  }

  // From the approval dialog: persist the decision, then close.
  const resolve = (decision: 'approved' | 'declined') => (entry: RosterMatch) => {
    persist([entry], decision)
    setDetail(null)
  }

  // Card badge state for the Queued column, keyed by leadId — only contacts the
  // founder has actually decided get a badge.
  const resolutions: Record<string, 'approved' | 'declined'> = {}
  for (const entry of visible) {
    const decision = entry.leadId ? decisionOf(entry) : undefined
    if (entry.leadId && decision) resolutions[entry.leadId] = decision
  }

  // Visible contacts not yet decided — what "Approve all" persists. Fund-level
  // placeholders (no contactId) and already-decided contacts are excluded;
  // empty while locked.
  const approvableEntries = locked
    ? []
    : visible.filter((entry) => entry.contactId && decisionOf(entry) === undefined)

  // Approve every not-yet-decided visible contact in one request, after the
  // founder confirms in the dialog.
  const approveAll = () => {
    persist(approvableEntries, 'approved')
    setConfirmApproveAll(false)
  }
  const hiddenCount = Math.max(totalContacts - visible.length, 0)

  const lockedFooter =
    locked && hiddenCount > 0 ? (
      <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 text-center">
        <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
          <Lock className="size-3.5 text-primary" />
          {hiddenCount} more contacts locked
        </div>
        <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
          Complete your profile to see all {totalContacts} investor contacts, each with a
          ready-to-send message.
        </p>
        <div className="mt-3 flex justify-center">
          <OutreachSetupCta />
        </div>
      </div>
    ) : undefined

  const unlockedFooter =
    !locked && hiddenCount > 0 ? (
      <p className="py-2 text-center text-[11.5px] text-muted-foreground">
        + {hiddenCount} more partner contacts across these funds · best-fit first
      </p>
    ) : undefined

  return (
    <section id={CONTACTS_BOARD_ID} className="mb-10 scroll-mt-24 space-y-5">
      <RosterKanban
        columns={[
          {
            tone: 'primary',
            title: 'Queued',
            subtitle: "Contacts we'll reach out to, best-fit first.",
            totalCount: contactsEstimated ? `~${totalContacts}` : totalContacts,
            entries: visible,
            onCardClick: setDetail,
            resolutions,
            // Per-card approve/decline inline on each card (mirrors the portfolio
            // board). Disabled while the list is locked behind the profile step.
            onApprove: locked ? undefined : (entry) => persist([entry], 'approved'),
            onDecline: locked ? undefined : (entry) => persist([entry], 'declined'),
            // Always available while the board is unlocked — opens a confirmation
            // dialog (which handles the "nothing left to approve" case itself).
            onApproveAll: locked ? undefined : () => setConfirmApproveAll(true),
            approveAllPending: approvableEntries.length,
            lockedCount: locked ? hiddenCount : 0,
            lockedFooter,
            footer: unlockedFooter,
          },
          {
            tone: 'blue',
            title: 'In progress',
            subtitle: 'Messages start going out once your campaign is live.',
            totalCount: 0,
            entries: [],
          },
          {
            tone: 'muted',
            title: 'Finished',
            subtitle: 'Contacts whose sequence has wrapped up.',
            totalCount: 0,
            entries: [],
          },
        ]}
      />

      <QueuedApprovalDialog
        investor={detail}
        messages={messagesQuery.data ?? null}
        onClose={() => setDetail(null)}
        onApprove={resolve('approved')}
        onDecline={resolve('declined')}
        onRemove={() => setDetail(null)}
      />

      <AlertDialog open={confirmApproveAll} onOpenChange={setConfirmApproveAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {approvableEntries.length > 0
                ? `Approve all ${approvableEntries.length} ${approvableEntries.length === 1 ? 'contact' : 'contacts'}?`
                : 'Nothing left to approve'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {approvableEntries.length > 0
                ? "Every contact still awaiting a decision will be approved. When your campaign launches, they're queued to send right away, skipping the per-contact review. You can still decline any of them individually before launch."
                : 'Every visible contact is already approved or declined. Tap a contact to change an individual decision.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {approvableEntries.length > 0 ? 'Cancel' : 'Close'}
            </AlertDialogCancel>
            {approvableEntries.length > 0 && (
              <AlertDialogAction onClick={approveAll}>
                Approve all {approvableEntries.length}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
